import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  Spin,
  Table,
  Input,
  Tag,
  Space,
  Tooltip,
  Statistic,
  message,
} from "antd";
import { useSelector } from "react-redux";
import {
  getEmailSequencesPaginated,
  deleteEmailSequenceById,
  stopUserEmailSequence,
  getEmailSequenceAnalytics,
} from "@api/email/emailSequence";

const { Search } = Input;

const EmailSequencesList = ({ history }) => {
  const { user } = useSelector((state) => state.auth);

  // Main state
  const [emailSequences, setEmailSequences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    totalSequences: 0,
  });

  // Quick view modal state  
  const [quickViewVisible, setQuickViewVisible] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Initial load
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const search = searchParams.get("search") || "";

    setSearchText(search);
    setPagination({ current: page, pageSize, totalSequences: 0 });
    fetchEmailSequences(page, pageSize, search);
  }, []);

  // Fetch sequences list
  const fetchEmailSequences = async (page, pageSize, searchText = "") => {
    setLoading(true);
    try {
      const res = await getEmailSequencesPaginated(page, pageSize, searchText);
      setEmailSequences(res.data.sequences || []);
      setPagination((p) => ({
        ...p,
        totalSequences: res.data.paginationData?.totalSequences,
        current: res.data.paginationData?.page,
        pageSize: res.data.paginationData?.perPage,
      }));
    } catch (err) {
      console.error("Fetch email sequences error", err);
      message.error("Failed to fetch email sequences");
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination
  const handlePaginationChange = (page, pageSize) => {
    const searchParams = new URLSearchParams();
    searchParams.set("page", page);
    searchParams.set("pageSize", pageSize);
    if (searchText) searchParams.set("search", searchText);

    history.push({ search: searchParams.toString() });
    fetchEmailSequences(page, pageSize, searchText);
  };

  // Handle search
  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    setPagination({ ...pagination, current: 1 });
    fetchEmailSequences(1, pagination.pageSize, searchValue);

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("pageSize", pagination.pageSize);
    if (searchValue) searchParams.set("search", searchValue);
    history.push({ search: searchParams.toString() });
  };

  // Sequence actions
  const handleSequenceUpdate = (id) => {
    const basePath =
      user.role === "superadmin" ? "/super-admin" : "/marketing-admin";
    history.push(`${basePath}/email/email-sequences/${id}/update`);
  };

  const handleSequenceDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this email sequence?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteEmailSequenceById(id);
          fetchEmailSequences(pagination.current, pagination.pageSize);
          message.success("Email sequence deleted successfully");
        } catch (error) {
          console.error("Error deleting email sequence", error);
          message.error("Failed to delete email sequence");
        }
      },
    });
  };

  const handleStopSequence = async (sequenceId, userId) => {
    try {
      await stopUserEmailSequence(sequenceId, userId);
      message.success("Sequence stopped successfully");
      fetchEmailSequences(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Error stopping sequence", error);
      message.error("Failed to stop sequence");
    }
  };

  const handleViewAnalytics = (sequence) => {
    const basePath =
      user.role === "superadmin" ? "/super-admin" : "/marketing-admin";
    history.push(`${basePath}/email/email-sequences/${sequence._id}/analytics`);
  };

  const handleQuickView = async (sequence) => {
    setSelectedSequence(sequence);
    setQuickViewVisible(true);
    setAnalyticsLoading(true);

    try {
      const response = await getEmailSequenceAnalytics(sequence._id);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error("Error fetching basic stats", error);
      message.error("Failed to fetch sequence stats");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Table columns configuration
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) => (
        <Tooltip title={name}>
          {name?.length > 25 ? `${name.substring(0, 25)}...` : name}
        </Tooltip>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <Tag
          color={
            type === "onboarding"
              ? "blue"
              : type === "engagement"
              ? "green"
              : type === "milestone"
              ? "gold"
              : type === "retention"
              ? "purple"
              : "red"
          }
        >
          {type}
        </Tag>
      ),
    },
    {
      title: "Trigger",
      dataIndex: "trigger",
      key: "trigger",
      render: (trigger) => <Tag color="blue">{trigger.eventType}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      render: (active) => (
        <Tag color={active ? "green" : "red"}>
          {active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: "Action",
      key: "action",
      width: 300,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => handleSequenceUpdate(record._id)}
          >
            Edit
          </Button>
          <Button
            type="danger"
            size="small"
            onClick={() => handleSequenceDelete(record._id)}
          >
            Delete
          </Button>
          <Button
            type="default"
            size="small"
            onClick={() => handleViewAnalytics(record)}
          >
            Analytics
          </Button>
          <Button
            type="ghost"
            size="small"
            onClick={() => handleQuickView(record)}
          >
            Quick View
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card style={{ borderRadius: "0", height: "100vh", overflowY: "hidden" }}>
        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Col>
            <h3>Email Sequences List</h3>
          </Col>
          <Col>
            <Space>
              <Button
                type="ghost"
                onClick={() => {
                  const basePath =
                    user.role === "superadmin"
                      ? "/super-admin"
                      : "/marketing-admin";
                  history.push(`${basePath}/email/html-templates`);
                }}
              >
                View HTML Templates
              </Button>

              {/* View Email Campaigns Button */}
              <Button
                type="ghost"
                onClick={() => {
                  if (user.role === "superadmin") {
                    history.push("/super-admin/email/email-campaigns");
                  } else if (user.role === "marketing") {
                    history.push("/marketing-admin/email/email-campaigns");
                  }
                }}
                style={{ marginRight: 10 }}
              >
                View Email Campaigns
              </Button>

              <Button
                type="primary"
                onClick={() => {
                  const basePath =
                    user.role === "superadmin"
                      ? "/super-admin"
                      : "/marketing-admin";
                  history.push(`${basePath}/email/email-sequences/create`);
                }}
              >
                Create Email Sequence
              </Button>

              <Search
                placeholder="Search by name"
                style={{ width: "300px" }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={handleSearch}
              />
            </Space>
          </Col>
        </Row>

        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={emailSequences}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.totalSequences,
            }}
            loading={loading}
            onChange={({ current, pageSize }) =>
              handlePaginationChange(current, pageSize)
            }
            rowKey={(record) => record._id}
            scroll={{ y: "calc(100vh - 240px)" }}
            tableLayout="fixed"
          />
        </Spin>
      </Card>

      {/* Quick View Modal */}
      <Modal
        title={`Quick View - ${selectedSequence?.name}`}
        visible={quickViewVisible}
        onCancel={() => setQuickViewVisible(false)}
        width={900}
        footer={null}
      >
        {selectedSequence && (
          <div>
            {/* Basic Information */}
            <Card title="Sequence Overview" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <div style={{ marginBottom: 12 }}>
                    <strong>Name:</strong> {selectedSequence.name}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <strong>Type:</strong>{" "}
                    <Tag
                      color={
                        selectedSequence.type === "onboarding"
                          ? "blue"
                          : selectedSequence.type === "engagement"
                          ? "green"
                          : selectedSequence.type === "milestone"
                          ? "gold"
                          : selectedSequence.type === "retention"
                          ? "purple"
                          : "red"
                      }
                    >
                      {selectedSequence.type}
                    </Tag>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <strong>Status:</strong>{" "}
                    <Tag color={selectedSequence.active ? "green" : "red"}>
                      {selectedSequence.active ? "Active" : "Inactive"}
                    </Tag>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 12 }}>
                    <strong>Created:</strong>{" "}
                    {new Date(selectedSequence.createdAt).toLocaleDateString()}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <strong>Last Updated:</strong>{" "}
                    {new Date(selectedSequence.updatedAt).toLocaleDateString()}
                  </div>
                  {selectedSequence.description && (
                    <div style={{ marginBottom: 12 }}>
                      <strong>Description:</strong> {selectedSequence.description}
                    </div>
                  )}
                </Col>
              </Row>
            </Card>

            {/* Trigger Configuration */}
            <Card title="Trigger Configuration" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Trigger Event:</strong>{" "}
                    <Tag color="blue">{selectedSequence.trigger?.eventType}</Tag>
                  </div>
                  {selectedSequence.trigger?.delay && (
                    <div style={{ marginBottom: 8 }}>
                      <strong>Initial Delay:</strong>{" "}
                      {selectedSequence.trigger.delay.value} {selectedSequence.trigger.delay.unit}
                    </div>
                  )}
                </Col>
                <Col span={12}>
                  {selectedSequence.trigger?.conditions && (
                    <div style={{ marginBottom: 8 }}>
                      <strong>Conditions:</strong>
                      <ul style={{ marginTop: 4, paddingLeft: 16 }}>
                        {Object.entries(selectedSequence.trigger.conditions).map(([key, value]) => (
                          <li key={key}>
                            {key}: {Array.isArray(value) ? value.join(', ') : String(value)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Col>
              </Row>
            </Card>

            {/* Email Sequence Steps */}
            <Card title="Email Sequence Steps" style={{ marginBottom: 16 }}>
              {selectedSequence.emails && selectedSequence.emails.length > 0 ? (
                <div>
                  <div style={{ marginBottom: 12, color: "#666" }}>
                    {selectedSequence.emails.length} emails in this sequence
                  </div>
                  {selectedSequence.emails.map((email, index) => (
                    <Card
                      key={index}
                      type="inner"
                      size="small"
                      style={{ marginBottom: 8 }}
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Tag color="blue">Step {index + 1}</Tag>
                          <span>{email.subject || 'Untitled Email'}</span>
                        </div>
                      }
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          {email.delay && (
                            <div style={{ marginBottom: 4 }}>
                              <strong>Delay:</strong> {email.delay.value} {email.delay.unit}
                              {index === 0 ? ' (after trigger)' : ' (after previous email)'}
                            </div>
                          )}
                          {email.fromName && (
                            <div style={{ marginBottom: 4 }}>
                              <strong>From:</strong> {email.fromName}
                            </div>
                          )}
                        </Col>
                        <Col span={12}>
                          {email.templateId && (
                            <div style={{ marginBottom: 4 }}>
                              <strong>Template:</strong> {email.templateId.name || 'Template Used'}
                            </div>
                          )}
                          <Tag size="small" color="geekblue">
                            {email.content ? 'Custom Content' : 'Template Content'}
                          </Tag>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                  No emails configured yet
                </div>
              )}
            </Card>

            {/* Segmentation & Targeting */}
            {selectedSequence.segmentation && (
              <Card title="Target Audience" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    {selectedSequence.segmentation.demographic && (
                      <div>
                        <strong>Demographics:</strong>
                        <ul style={{ marginTop: 4, paddingLeft: 16 }}>
                          {selectedSequence.segmentation.demographic.subscriptionTier && (
                            <li>Subscription: {selectedSequence.segmentation.demographic.subscriptionTier.join(', ')}</li>
                          )}
                          {selectedSequence.segmentation.demographic.userRole && (
                            <li>Role: {selectedSequence.segmentation.demographic.userRole.join(', ')}</li>
                          )}
                          {selectedSequence.segmentation.demographic.accountType && (
                            <li>Account Type: {selectedSequence.segmentation.demographic.accountType.join(', ')}</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </Col>
                  <Col span={12}>
                    {selectedSequence.segmentation.behavioral && (
                      <div>
                        <strong>Behavioral:</strong>
                        <ul style={{ marginTop: 4, paddingLeft: 16 }}>
                          {selectedSequence.segmentation.behavioral.usagePattern && (
                            <li>Usage: {selectedSequence.segmentation.behavioral.usagePattern.join(', ')}</li>
                          )}
                          {selectedSequence.segmentation.behavioral.engagementLevel && (
                            <li>Engagement: {selectedSequence.segmentation.behavioral.engagementLevel.join(', ')}</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </Col>
                </Row>
              </Card>
            )}

            {/* Quick Stats */}
            <Spin spinning={analyticsLoading}>
              {analyticsData && (
                <Card title="Quick Stats">
                  <Row gutter={16}>
                    <Col span={6}>
                      <Statistic
                        title="Total Recipients"
                        value={analyticsData.summary?.totalRecipients || analyticsData.totalUsers || 0}
                        prefix="👥"
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Completed"
                        value={analyticsData.summary?.status?.completed || analyticsData.completed || 0}
                        valueStyle={{ color: "#3f8600" }}
                        prefix="✅"
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Active"
                        value={analyticsData.summary?.status?.inProgress || analyticsData.inProgress || 0}
                        valueStyle={{ color: "#1890ff" }}
                        prefix="🔄"
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Completion Rate"
                        value={
                          analyticsData.summary?.totalRecipients > 0
                            ? Math.round(
                                ((analyticsData.summary?.status?.completed || analyticsData.completed || 0) /
                                  analyticsData.summary.totalRecipients) *
                                  100
                              )
                            : 0
                        }
                        suffix="%"
                        valueStyle={{ color: "#722ed1" }}
                        prefix="📊"
                      />
                    </Col>
                  </Row>
                </Card>
              )}
            </Spin>
          </div>
        )}
      </Modal>
    </>
  );
};

export default EmailSequencesList;
